<!-- Overlay -->
<div class="ai-panel-overlay"
     [class.open]="aiPanelOpen"
     *ngIf="!shouldHidePanel"
     (click)="closePanel()">
</div>

<!-- Panel de IA - Chat Style -->
<div class="ai-panel" *ngIf="!shouldHidePanel" [class.open]="aiPanelOpen" [class.with-preview]="showPagePreview && pendingPageChanges">

    <!-- Header -->
    <div class="ai-panel-header">
        <div class="ai-header-left">
            <i class="fa-solid fa-wand-magic-sparkles ai-header-sparkle"></i>
            <span class="ai-header-title">4AI Assistant</span>
        </div>
        <div class="ai-header-actions">
            <button class="ai-header-btn" (click)="newChat()" title="New Chat">
                <i class="fas fa-plus"></i>
            </button>
            <button class="ai-header-btn" (click)="toggleHistory()" title="Chat History"
                    [class.active]="showHistory">
                <i class="fas fa-clock"></i>
            </button>
            <div class="ai-token-dropdown-wrapper">
                <button class="ai-header-btn" (click)="showTokenDropdown = !showTokenDropdown" title="Token Usage"
                        [class.active]="showTokenDropdown">
                    <i class="fas fa-microchip"></i>
                </button>
                <div class="ai-token-dropdown" *ngIf="showTokenDropdown">
                    <div class="ai-token-dropdown-title">
                        <i class="fas fa-microchip"></i> Token Usage
                    </div>
                    <div class="ai-token-dropdown-stats">
                        <div class="ai-token-dropdown-stat">
                            <span class="ai-token-dropdown-label">Session</span>
                            <span class="ai-token-dropdown-value">{{ sessionTokens | number }}</span>
                        </div>
                        <button class="ai-token-dropdown-reset" (click)="resetTokenSession()" title="Reset session counter">
                            <i class="fas fa-redo-alt"></i>
                        </button>
                        <div class="ai-token-dropdown-divider"></div>
                        <div class="ai-token-dropdown-stat">
                            <span class="ai-token-dropdown-label">All time</span>
                            <span class="ai-token-dropdown-value">{{ userTotalTokens | number }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <button class="ai-header-btn" (click)="closePanel()" title="Close">
                <i class="fas fa-times"></i>
            </button>
        </div>
    </div>

    <!-- Context badge -->
    <div class="ai-context-badge" *ngIf="isPageEditorMode">
        <i class="fas fa-file-alt"></i>
        <span class="ai-context-label">Editing: <strong>{{ activeContext.pageName || 'Preference Page' }}</strong></span>
        <span class="ai-context-mode-tag">Page Editor</span>
    </div>

    <!-- Sessions list (togglable) -->
    <div class="ai-sessions-list" *ngIf="showHistory">
        <div class="ai-session-empty" *ngIf="chatSessions.length === 0">
            {{ 'PREFERENCES.AI_NO_PREVIOUS_CONVERSATIONS' | translate }}
        </div>
        <div class="ai-session-item"
             *ngFor="let session of chatSessions; trackBy: trackBySessionId"
             [class.active]="session.id === currentSessionId"
             (click)="loadSession(session.id)">
            <span class="session-title">{{ session.title }}</span>
            <button class="session-delete" (click)="deleteSession(session.id, $event)">
                <i class="fas fa-times"></i>
            </button>
        </div>
    </div>

    <!-- Messages area -->
    <div class="ai-messages" #messagesContainer>
        <!-- Empty state -->
        <div class="ai-empty-state" *ngIf="messages.length === 0">
            <div class="ai-empty-icon">
                <i class="fa-solid fa-wand-magic-sparkles"></i>
            </div>
            <span class="ai-empty-title">{{ 'PREFERENCES.AI_HOW_CAN_I_HELP' | translate }}</span>
            <span class="ai-empty-subtitle">{{ 'PREFERENCES.AI_ASK_ABOUT_PREFERENCES' | translate }}</span>
            <div class="ai-quick-prompts">
                <button class="ai-quick-btn" *ngFor="let qp of activeQuickPrompts" (click)="sendQuickPrompt(qp.prompt)">
                    <i class="fas" [ngClass]="qp.icon"></i> {{ qp.label }}
                </button>
            </div>
        </div>

        <!-- Chat messages -->
        <ng-container *ngFor="let msg of messages">
            <div class="ai-msg-row" [ngClass]="msg.role">
                <div class="ai-msg-avatar">
                    <i *ngIf="msg.role === 'assistant'" class="fa-solid fa-wand-magic-sparkles"></i>
                    <i *ngIf="msg.role === 'user'" class="fas fa-user"></i>
                </div>
                <div class="ai-msg-content">
                    <div class="ai-msg-bubble" [innerHTML]="msg.formattedContent || msg.content"></div>
                    <div class="ai-msg-time">{{ msg.timestamp | date:'h:mm a' }}</div>
                </div>
            </div>
        </ng-container>

        <!-- Typing indicator -->
        <div class="ai-msg-row assistant" *ngIf="isGenerating">
            <div class="ai-msg-avatar">
                <i class="fa-solid fa-wand-magic-sparkles"></i>
            </div>
            <div class="ai-msg-content">
                <div class="ai-msg-bubble">
                    <div class="ai-typing">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Preview panel (inline for page results) -->
    <div class="ai-preview-inline" *ngIf="showPreviewPanel && aiPage?.intent === 'create_page' && generatedHtml">
        <div class="ai-preview-header">
            <span>{{ 'PREFERENCES.AI_PAGE_PREVIEW' | translate }}</span>
            <button class="ai-header-btn" (click)="showPreviewPanel = false">
                <i class="fas fa-times"></i>
            </button>
        </div>
        <iframe [srcdoc]="generatedHtml" width="100%" height="300px" frameborder="0"></iframe>
        <div class="ai-preview-actions">
            <button class="ai-confirm-btn" (click)="confirmPageCreation()">
                <i class="fas fa-check"></i> {{ 'PREFERENCES.AI_CONFIRM_PAGE' | translate }}
            </button>
        </div>
    </div>

    <!-- Data preview panel (for expiring permissions / large datasets) -->
    <div class="ai-data-preview" *ngIf="showDataPreview && dataPreviewRows.length > 0">
        <div class="ai-preview-header">
            <span>{{ dataPreviewTitle }} ({{ dataPreviewRows.length }})</span>
            <div class="ai-preview-header-actions">
                <button class="ai-export-btn" (click)="exportToCsv()" title="Export CSV">
                    <i class="fas fa-download"></i> CSV
                </button>
                <button class="ai-header-btn" (click)="showDataPreview = false">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>
        <div class="ai-data-table-wrap">
            <table class="ai-data-table">
                <thead>
                    <tr>
                        <th *ngFor="let col of dataPreviewColumns">{{ col }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of dataPreviewRows">
                        <td *ngFor="let col of dataPreviewColumns">{{ row[col] ?? '' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Agent confirmation card -->
    <div class="agent-confirmation-card" *ngIf="pendingAgentConfirmation">
        <div class="agent-confirmation-header">
            <i class="fas fa-shield-alt"></i>
            <span>{{ 'AGENT.CONFIRMATION_REQUIRED' | translate }}</span>
        </div>
        <div class="agent-confirmation-summary">
            {{ pendingAgentConfirmation.summary }}
        </div>
        <div class="agent-confirmation-actions">
            <button class="ai-apply-btn" (click)="confirmAgentAction()" [disabled]="isGenerating">
                <i class="fas fa-check"></i> {{ 'AGENT.CONFIRM' | translate }}
            </button>
            <button class="ai-reject-btn" (click)="rejectAgentAction()" [disabled]="isGenerating">
                <i class="fas fa-times"></i> {{ 'AGENT.CANCEL' | translate }}
            </button>
        </div>
    </div>

    <!-- Input area -->
    <div class="ai-input-area">
        <!-- Attached images strip -->
        <div class="ai-attached-images" *ngIf="attachedImages.length > 0">
            <div class="ai-attached-thumb" *ngFor="let img of attachedImages; let i = index">
                <img [src]="getImageThumbnail(img)" [alt]="img.fileName || 'Image'">
                <button class="ai-thumb-remove" (click)="removeImage(i)" title="Remove">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>

        <div class="ai-input-row">
            <!-- Hidden file input -->
            <input type="file" #fileInput accept="image/*" multiple (change)="onFileSelected($event)" style="display: none;">

            <!-- Image upload button (only in page-editor mode) -->
            <button class="ai-input-action-btn"
                    *ngIf="isPageEditorMode"
                    (click)="triggerFileInput()"
                    [disabled]="isGenerating || attachedImages.length >= maxImages"
                    title="Attach image">
                <i class="fas fa-image"></i>
            </button>

            <!-- Speech-to-text button -->
            <button class="ai-input-action-btn ai-speech-btn"
                    *ngIf="speechSupported"
                    (click)="toggleSpeechToText()"
                    [class.listening]="isListening"
                    [disabled]="isGenerating"
                    [title]="isListening ? 'Stop listening' : 'Voice input'">
                <i class="fas" [ngClass]="isListening ? 'fa-stop' : 'fa-microphone'"></i>
            </button>

            <!-- Textarea with autocomplete -->
            <div class="ai-input-wrapper">
                <textarea [(ngModel)]="aiPrompt"
                          [placeholder]="'PREFERENCES.AI_INPUT_PLACEHOLDER' | translate"
                          rows="1"
                          (keydown)="handleKeyDown($event)"
                          (ngModelChange)="onPromptInput()"
                          [disabled]="isGenerating"
                          #promptInput></textarea>
                <!-- Autocomplete dropdown -->
                <div class="ai-autocomplete" *ngIf="showAutocomplete && autocompleteItems.length > 0">
                    <button class="ai-autocomplete-item"
                            *ngFor="let item of autocompleteItems; let i = index"
                            [class.active]="i === autocompleteIndex"
                            (mousedown)="selectAutocompleteItem(item)">
                        <span class="ai-autocomplete-label">{{ item.label }}</span>
                        <span class="ai-autocomplete-detail"
                              [ngClass]="'cat-' + item.category">{{ item.detail }}</span>
                    </button>
                </div>
            </div>

            <button class="ai-send-btn"
                    (click)="sendMessage()"
                    [disabled]="isGenerating || !aiPrompt?.trim()"
                    title="Send">
                <i class="fas fa-paper-plane"></i>
            </button>
        </div>
    </div>
</div>

<!-- Page editor preview side panel (slides out to the right of the main panel) -->
<div class="ai-preview-side-panel"
     *ngIf="!shouldHidePanel"
     [class.open]="aiPanelOpen && showPagePreview && (pendingPageChanges || isNewPagePreview)">
    <div class="ai-preview-side-header">
        <span>{{ isNewPagePreview ? 'Page Preview' : 'Page Changes Preview' }}</span>
        <div class="ai-preview-side-header-actions">
            <button class="ai-preview-tab-btn" [class.active]="pagePreviewTab === 'preview'" (click)="setPreviewTab('preview')">
                <i class="fas fa-eye"></i> Preview
            </button>
            <button class="ai-preview-tab-btn" [class.active]="pagePreviewTab === 'code'" (click)="pagePreviewTab = 'code'">
                <i class="fas fa-code"></i> Code
            </button>
            <button class="ai-preview-side-close" (click)="rejectPageChanges()" title="Dismiss">
                <i class="fas fa-times"></i>
            </button>
        </div>
    </div>
    <div class="ai-preview-side-content">
        <iframe *ngIf="pagePreviewTab === 'preview'" #previewIframe class="ai-preview-side-iframe"></iframe>
        <pre *ngIf="pagePreviewTab === 'code'" class="ai-preview-side-code">{{ pagePreviewCode }}</pre>
    </div>
    <div class="ai-preview-side-actions" *ngIf="!isNewPagePreview">
        <button class="ai-apply-btn" (click)="applyPageChanges()">
            <i class="fas fa-check"></i> Apply Changes
        </button>
        <button class="ai-reject-btn" (click)="rejectPageChanges()">
            <i class="fas fa-times"></i> Discard
        </button>
    </div>
</div>
